import { assign, enqueueActions, fromCallback, fromPromise, setup } from 'xstate';
import { MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import {
  buildDateRange,
  buildMonthGrid,
  buildWeekDays,
  getBrowserTimezone,
  parseISODate,
  toISODate,
  type CalendarViewMode,
} from '~/features/shared/calendar/dateUtils';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { AvailabilityConflictError, CALENDAR_API } from './api';
import { MAX_BULK_DATES } from './constants';
import type {
  AvailabilityConflict,
  AvailabilityDay,
  AvailabilityRule,
  AvailabilityScope,
  CalendarContract,
  ProviderServiceListingSummary,
  WritableAvailabilityEntry,
  WritableAvailabilityException,
  WritableAvailabilityRule,
  WritableAvailabilityRulePatch,
  WritableBatchDay,
} from './types';

export interface AvailabilityCalendarInput {
  initialScope?: AvailabilityScope;
  today?: string;
  timezone?: string | null;
}

export interface AvailabilityCalendarContext {
  anchorISO: string;
  conflict: AvailabilityConflict | null;
  /**
   * [MCV-1821] The provider's pending and active contracts starting in the loaded range,
   * unfiltered by scope (the page shows JOB contracts in every scope and SERVICE contracts in
   * their own listing's). Read-only decoration: a failed read leaves this empty and sets
   * `contractsError`, but never blocks availability editing.
   */
  contracts: CalendarContract[];
  contractsError: string | null;
  /**
   * The visible range (`start|end`) the contracts were last requested for. Contracts reload only
   * when the visible range differs from this or the last read of it failed, so availability
   * mutations and scope changes never re-read them once they have loaded.
   */
  contractsRangeKey: string | null;
  days: AvailabilityDay[];
  error: string | null;
  /**
   * True only while `days`/`rules` reflect a successful read of the current
   * range and scope. A failed load leaves an empty projection that looks
   * identical to a genuinely empty calendar, so mutations must not run against
   * it — clearing or saving a day would delete server rows the GET never
   * returned.
   */
  hasAuthoritativeLoad: boolean;
  listings: ProviderServiceListingSummary[];
  notice: string | null;
  rules: AvailabilityRule[];
  scope: AvailabilityScope;
  /**
   * The date(s) the editor panel is open against, snapshotted from
   * `selectedDates` at the moment it opened. Empty means the editor is
   * closed. Changing the underlying selection (`ON_TOGGLE_DATE`) closes it,
   * the same as before MCV-1691's multi-date editor: an open editor's
   * dates/draft must never silently drift out from under it.
   */
  editorDates: string[];
  selectedDates: string[];
  selectionAnchor: string | null;
  timezone: string | null;
  todayISO: string;
  viewMode: CalendarViewMode;
}

export type AvailabilityCalendarEvent =
  | { type: 'ON_LOAD' }
  | { type: 'ON_RETRY' }
  | { type: 'ON_SET_ANCHOR'; anchorISO: string }
  | { type: 'ON_SET_VIEW'; viewMode: CalendarViewMode }
  | { type: 'ON_SET_SCOPE'; scope: AvailabilityScope }
  | { type: 'ON_TOGGLE_DATE'; date: string; shiftKey?: boolean }
  | { type: 'ON_CLEAR_SELECTION' }
  | { type: 'ON_OPEN_EDITOR' }
  | { type: 'ON_CLOSE_EDITOR' }
  | { type: 'ON_CLEAR_NOTICE' }
  | {
      type: 'ON_CONTRACTS_LOADED';
      contracts: CalendarContract[];
      error: string | null;
      rangeKey: string;
    }
  | { type: 'ON_BULK_MARK'; status: 'AVAILABLE' | 'UNAVAILABLE' }
  | { type: 'ON_CLEAR_DATES' }
  | { type: 'ON_CLEAR_DAY'; date: string }
  | { type: 'ON_SAVE_DAY'; date: string; entries: WritableAvailabilityEntry[] }
  | { type: 'ON_SAVE_DAYS'; days: WritableBatchDay[] }
  | {
      type: 'ON_SAVE_RULE';
      rule: WritableAvailabilityRule | WritableAvailabilityRulePatch;
      ruleId?: number;
    }
  | { type: 'ON_DELETE_RULE'; ruleId: number }
  | {
      type: 'ON_SAVE_EXCEPTION';
      ruleId: number;
      exception: WritableAvailabilityException;
    };

export const getVisibleDates = (anchorISO: string, viewMode: CalendarViewMode): Date[] => {
  const anchor = parseISODate(anchorISO);
  return viewMode === 'month' ? buildMonthGrid(anchor) : buildWeekDays(anchor);
};

const getVisibleRange = (context: AvailabilityCalendarContext) => {
  const dates = getVisibleDates(context.anchorISO, context.viewMode);
  return {
    start: toISODate(dates[0]),
    end: toISODate(dates[dates.length - 1]),
  };
};

// A day with no entries is a normal, common state (nothing has been entered
// for it yet) -- `daysByDate` being empty for a date does NOT mean the date
// wasn't loaded. What makes a date's data authoritative is whether it falls
// inside the range the last successful load actually covered. Navigating away
// (item 1) can leave a selected date outside that range, and that is the
// case the single-day editor's pre-fill/PUT-replace must never open against.
const isWithinLoadedRange = (context: AvailabilityCalendarContext, date: string): boolean => {
  const { start, end } = getVisibleRange(context);
  return date >= start && date <= end;
};

// The editor has exactly two modes. A single in-range date pre-fills from
// that day's loaded data and PUTs a full replacement for it (unchanged single-
// day behavior). Everything else -- two or more dates, or the one lone date
// that survived navigation into an out-of-range month (MCV-1674 item 1) --
// opens a blank multi-date "template" editor that batch-replaces every listed
// date instead. `editorDates` is frozen at ON_OPEN_EDITOR, so this only needs
// today's anchor/viewMode, which cannot change while the editor is open
// (navigating away closes it).
export const isSingleDateEditor = (context: AvailabilityCalendarContext): boolean =>
  context.editorDates.length === 1 && isWithinLoadedRange(context, context.editorDates[0]);

const updateSelection = (
  context: AvailabilityCalendarContext,
  event: Extract<AvailabilityCalendarEvent, { type: 'ON_TOGGLE_DATE' }>
): Partial<AvailabilityCalendarContext> => {
  const nextSelectedDates = new Set(context.selectedDates);
  let nextAnchor = event.date;

  if (event.shiftKey && context.selectionAnchor) {
    buildDateRange(context.selectionAnchor, event.date).forEach((date) =>
      nextSelectedDates.add(date)
    );
    nextAnchor = context.selectionAnchor;
  } else if (nextSelectedDates.has(event.date)) {
    nextSelectedDates.delete(event.date);
  } else {
    nextSelectedDates.add(event.date);
  }

  // The bulk mark/clear endpoints send the *entire* selection in one request
  // and the server 400s above MAX_BULK_DATES. A shift-click range can now
  // cross months (selection survives navigation), so it can cross that cap in
  // a single gesture. Refuse rather than silently truncate: the prior
  // selection is left exactly as it was.
  if (nextSelectedDates.size > MAX_BULK_DATES) {
    return {
      error: `That selection has ${nextSelectedDates.size} dates, more than the ${MAX_BULK_DATES}-date limit for a single update. Clear some dates (or use Clear selection) and try again.`,
    };
  }

  return {
    error: null,
    // Closes any open editor, single- or multi-date: its dates/draft must
    // never silently drift out from under an in-progress edit.
    editorDates: [],
    selectedDates: [...nextSelectedDates].sort(),
    selectionAnchor: nextAnchor,
  };
};

const contractsRangeKeyFor = (context: AvailabilityCalendarContext): string => {
  const { start, end } = getVisibleRange(context);
  return `${start}|${end}`;
};

const messageForError = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unable to update availability. Please try again.';

export const availabilityCalendarMachine = setup({
  types: {
    context: {} as AvailabilityCalendarContext,
    events: {} as AvailabilityCalendarEvent,
    input: {} as AvailabilityCalendarInput,
  },
  actions: {
    // Contracts are decoration on the grid, so they load beside the calendar rather than inside
    // it: a slow or hung contracts read must never hold the availability view back.
    requestContractsForRange: enqueueActions(({ context, enqueue }) => {
      const rangeKey = contractsRangeKeyFor(context);
      // An unchanged range is not re-read, unless its last read failed: then the next load
      // (a mutation, a scope change or Retry) tries it again.
      if (context.contractsRangeKey === rangeKey && !context.contractsError) return;
      enqueue.stopChild('contracts');
      enqueue.spawnChild('loadContracts', {
        id: 'contracts',
        input: { ...getVisibleRange(context), rangeKey },
      });
      enqueue.assign({ contractsRangeKey: rangeKey, contractsError: null });
    }),
    recordMutationError: assign(({ event }) => {
      const error = (event as unknown as { error: unknown }).error;
      return {
        conflict: error instanceof AvailabilityConflictError ? error.conflict : null,
        error: messageForError(error),
      };
    }),
  },
  actors: {
    loadCalendar: fromPromise(
      async ({ input }: { input: { end: string; scope: AvailabilityScope; start: string } }) => {
        const [range, rules, listings] = await Promise.all([
          CALENDAR_API.getRange(input),
          CALENDAR_API.getRules(),
          CALENDAR_API.getServiceListings(),
        ]);
        return { range, rules, listings };
      }
    ),
    loadContracts: fromCallback<
      AvailabilityCalendarEvent,
      { end: string; rangeKey: string; start: string }
    >(({ input, sendBack }) => {
      const controller = new AbortController();
      withRequestDeadline(
        (signal) => CALENDAR_API.getContracts({ start: input.start, end: input.end }, signal),
        MARKET_READ_TIMEOUT_MS,
        controller.signal
      ).then(
        (contracts) =>
          sendBack({
            type: 'ON_CONTRACTS_LOADED',
            contracts,
            error: null,
            rangeKey: input.rangeKey,
          }),
        (error: unknown) => {
          if (controller.signal.aborted) return;
          sendBack({
            type: 'ON_CONTRACTS_LOADED',
            contracts: [],
            error: messageForError(error),
            rangeKey: input.rangeKey,
          });
        }
      );
      return () => controller.abort();
    }),
    bulkMark: fromPromise(
      async ({
        input,
      }: {
        input: {
          dates: string[];
          scope: AvailabilityScope;
          status: 'AVAILABLE' | 'UNAVAILABLE';
          timezone: string;
        };
      }) => CALENDAR_API.bulkMark(input)
    ),
    clearDates: fromPromise(
      async ({
        input,
      }: {
        input: { dates: string[]; scope: AvailabilityScope; timezone: string };
      }) => {
        const results = await Promise.allSettled(
          input.dates.map((date) =>
            CALENDAR_API.deleteDay({ date, scope: input.scope, timezone: input.timezone })
          )
        );
        const failure = results.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected'
        );
        if (failure) throw failure.reason;
        return results.map((result) => (result as PromiseFulfilledResult<unknown>).value);
      }
    ),
    saveDay: fromPromise(
      async ({
        input,
      }: {
        input: {
          date: string;
          entries: WritableAvailabilityEntry[];
          scope: AvailabilityScope;
          timezone: string;
        };
      }) => CALENDAR_API.putDay(input)
    ),
    saveDays: fromPromise(
      async ({
        input,
      }: {
        input: {
          days: WritableBatchDay[];
          scope: AvailabilityScope;
          timezone: string;
        };
      }) => CALENDAR_API.batchReplaceDays(input)
    ),
    saveRule: fromPromise(
      async ({
        input,
      }: {
        input: {
          rule: WritableAvailabilityRule | WritableAvailabilityRulePatch;
          ruleId?: number;
        };
      }) =>
        input.ruleId == null
          ? CALENDAR_API.createRule(input.rule as WritableAvailabilityRule)
          : CALENDAR_API.updateRule(input.ruleId, input.rule)
    ),
    deleteRule: fromPromise(async ({ input }: { input: { ruleId: number } }) =>
      CALENDAR_API.deleteRule(input.ruleId)
    ),
    saveException: fromPromise(
      async ({
        input,
      }: {
        input: {
          exception: WritableAvailabilityException;
          ruleId: number;
        };
      }) => CALENDAR_API.createException(input.ruleId, input.exception)
    ),
  },
  guards: {
    // Every mutation guard requires an authoritative load. Without it a failed
    // range read presents as an empty calendar and a clear or save would delete
    // rows that were merely hidden.
    canMutateSelection: ({ context }) =>
      context.hasAuthoritativeLoad &&
      context.scope.kind !== 'all' &&
      context.selectedDates.length > 0 &&
      Boolean(context.timezone),
    canMutateScope: ({ context }) => context.hasAuthoritativeLoad && context.scope.kind !== 'all',
    canMutateScopeWithTimezone: ({ context }) =>
      context.hasAuthoritativeLoad && context.scope.kind !== 'all' && Boolean(context.timezone),
    // Selection is not itself destructive, but without an authoritative load
    // there is nothing real to select: the days are an empty projection, and
    // every mutation the selection feeds would be dropped by the guards above.
    // Refusing the selection is what makes that visible instead of silent.
    canSelectDates: ({ context }) => context.hasAuthoritativeLoad,
  },
}).createMachine({
  id: 'availabilityCalendar',
  context: ({ input }) => {
    const todayISO = input.today ?? toISODate(new Date());
    return {
      anchorISO: todayISO,
      conflict: null,
      contracts: [],
      contractsError: null,
      contractsRangeKey: null,
      days: [],
      editorDates: [],
      error: null,
      hasAuthoritativeLoad: false,
      listings: [],
      notice: null,
      rules: [],
      scope: input.initialScope ?? { kind: 'all' },
      selectedDates: [],
      selectionAnchor: null,
      timezone: input.timezone === undefined ? getBrowserTimezone() : input.timezone,
      todayISO,
      viewMode: 'month',
    };
  },
  initial: 'idle',
  on: {
    // A response for a range the calendar has since left is dropped.
    ON_CONTRACTS_LOADED: {
      guard: ({ context, event }) => event.rangeKey === context.contractsRangeKey,
      actions: assign({
        contracts: ({ event }) => event.contracts,
        contractsError: ({ event }) => event.error,
      }),
    },
  },
  states: {
    idle: {
      on: { ON_LOAD: { target: 'loading' } },
    },
    loading: {
      entry: 'requestContractsForRange',
      invoke: {
        src: 'loadCalendar',
        input: ({ context }) => ({ ...getVisibleRange(context), scope: context.scope }),
        onDone: {
          target: 'ready',
          actions: assign({
            days: ({ event }) => event.output.range.days,
            rules: ({ event }) => event.output.rules,
            listings: ({ event }) => event.output.listings,
            hasAuthoritativeLoad: true,
          }),
        },
        onError: {
          target: 'ready',
          actions: assign({
            days: [],
            rules: [],
            editorDates: [],
            selectedDates: [],
            selectionAnchor: null,
            error: ({ event }) => messageForError(event.error),
            conflict: null,
            hasAuthoritativeLoad: false,
          }),
        },
      },
    },
    ready: {
      on: {
        ON_RETRY: {
          target: 'loading',
          actions: assign({ error: null, conflict: null }),
        },
        ON_SET_ANCHOR: {
          target: 'loading',
          actions: assign({
            anchorISO: ({ event }) => event.anchorISO,
            error: null,
            conflict: null,
            // selectedDates/selectionAnchor deliberately survive navigation
            // (MCV-1674): only the open editor closes, since it pre-fills
            // from the range that is about to be replaced.
            editorDates: [],
          }),
        },
        ON_SET_VIEW: {
          target: 'loading',
          actions: assign({
            viewMode: ({ event }) => event.viewMode,
            error: null,
            conflict: null,
            // Same as ON_SET_ANCHOR: keep the selection, close the editor.
            editorDates: [],
          }),
        },
        ON_SET_SCOPE: {
          target: 'loading',
          actions: assign({
            scope: ({ event }) => event.scope,
            selectedDates: [],
            editorDates: [],
            selectionAnchor: null,
            error: null,
            conflict: null,
          }),
        },
        ON_TOGGLE_DATE: {
          guard: 'canSelectDates',
          actions: assign(({ context, event }) => updateSelection(context, event)),
        },
        ON_CLEAR_SELECTION: {
          // `selectedDate` (the open editor) is deliberately left alone: it
          // is only ever non-null while the editor is open, and an unsaved
          // draft must never be dropped by a stray click-off/Escape. When the
          // editor is closed it is already null, so there is nothing to do.
          actions: assign({ selectedDates: [], selectionAnchor: null }),
        },
        ON_OPEN_EDITOR: {
          // Any non-empty selection may open the editor now (MCV-1691): one
          // in-range date still means the single-day PUT flow, but two or
          // more dates -- or the one date that survived navigation outside
          // the loaded range -- now open the multi-date template editor
          // instead of being refused. See `isSingleDateEditor`.
          //
          // The read-only 'all' scope is the one exception (C2 regression):
          // it has no template to save, so the blank multi-date editor would
          // just show an empty draft over a real saved entry. There, Edit
          // ("View availability") opens only for exactly one selected date
          // that is inside the loaded range -- the pre-MCV-1691 behavior --
          // so it always pre-fills from real data instead of opening empty.
          guard: ({ context }) =>
            context.hasAuthoritativeLoad &&
            context.selectedDates.length > 0 &&
            (context.scope.kind !== 'all' ||
              (context.selectedDates.length === 1 &&
                isWithinLoadedRange(context, context.selectedDates[0]))),
          actions: assign({ editorDates: ({ context }) => context.selectedDates }),
        },
        ON_CLOSE_EDITOR: { actions: assign({ editorDates: [] }) },
        ON_CLEAR_NOTICE: { actions: assign({ notice: null }) },
        ON_BULK_MARK: {
          target: 'bulkSaving',
          guard: 'canMutateSelection',
          actions: assign({ error: null, conflict: null, notice: null }),
        },
        ON_CLEAR_DATES: {
          target: 'clearing',
          guard: 'canMutateSelection',
          actions: assign({ error: null, conflict: null, notice: null }),
        },
        ON_CLEAR_DAY: {
          target: 'clearing',
          guard: 'canMutateScopeWithTimezone',
          actions: assign({ error: null, conflict: null, notice: null }),
        },
        ON_SAVE_DAY: {
          target: 'daySaving',
          guard: 'canMutateScopeWithTimezone',
          actions: assign({ error: null, conflict: null, notice: null }),
        },
        ON_SAVE_DAYS: {
          target: 'daysSaving',
          guard: 'canMutateScopeWithTimezone',
          actions: assign({ error: null, conflict: null, notice: null }),
        },
        ON_SAVE_RULE: {
          target: 'ruleSaving',
          guard: 'canMutateScopeWithTimezone',
          actions: assign({ error: null, conflict: null, notice: null }),
        },
        ON_DELETE_RULE: {
          target: 'ruleDeleting',
          guard: 'canMutateScope',
          actions: assign({ error: null, conflict: null, notice: null }),
        },
        ON_SAVE_EXCEPTION: {
          target: 'exceptionSaving',
          guard: 'canMutateScopeWithTimezone',
          actions: assign({ error: null, conflict: null, notice: null }),
        },
      },
    },
    bulkSaving: {
      invoke: {
        src: 'bulkMark',
        input: ({ context, event }) => ({
          dates: context.selectedDates,
          scope: context.scope,
          status: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_BULK_MARK' }>).status,
          timezone: context.timezone as string,
        }),
        onDone: {
          target: 'loading',
          actions: assign({ notice: 'Availability updated.' }),
        },
        onError: { target: 'ready', actions: 'recordMutationError' },
      },
    },
    clearing: {
      invoke: {
        src: 'clearDates',
        input: ({ context, event }) => ({
          dates: event.type === 'ON_CLEAR_DAY' ? [event.date] : context.selectedDates,
          scope: context.scope,
          timezone: context.timezone as string,
        }),
        onDone: { target: 'loading', actions: assign({ notice: 'Availability cleared.' }) },
        // Some dates may have committed before a sibling request failed.
        // Reload only after every request settles so the view reflects the
        // authoritative partial result while retaining the error message.
        onError: { target: 'loading', actions: 'recordMutationError' },
      },
    },
    daySaving: {
      invoke: {
        src: 'saveDay',
        input: ({ context, event }) => ({
          date: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_SAVE_DAY' }>).date,
          entries: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_SAVE_DAY' }>).entries,
          scope: context.scope,
          timezone: context.timezone as string,
        }),
        onDone: { target: 'loading', actions: assign({ notice: 'Day details saved.' }) },
        onError: { target: 'ready', actions: 'recordMutationError' },
      },
    },
    daysSaving: {
      invoke: {
        src: 'saveDays',
        input: ({ context, event }) => ({
          days: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_SAVE_DAYS' }>).days,
          scope: context.scope,
          timezone: context.timezone as string,
        }),
        // Unlike the single-day PUT (which leaves the editor open, now showing
        // the reloaded server data), a successful batch replace closes it: it
        // started blank and targeted N dates at once, so there is no single
        // freshly-saved day left to keep editing in place.
        onDone: {
          target: 'loading',
          actions: assign({ notice: 'Availability updated.', editorDates: [] }),
        },
        onError: { target: 'ready', actions: 'recordMutationError' },
      },
    },
    ruleSaving: {
      invoke: {
        src: 'saveRule',
        input: ({ event }) => ({
          rule: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_SAVE_RULE' }>).rule,
          ruleId: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_SAVE_RULE' }>).ruleId,
        }),
        onDone: { target: 'loading', actions: assign({ notice: 'Recurring availability saved.' }) },
        onError: { target: 'ready', actions: 'recordMutationError' },
      },
    },
    ruleDeleting: {
      invoke: {
        src: 'deleteRule',
        input: ({ event }) => ({
          ruleId: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_DELETE_RULE' }>).ruleId,
        }),
        onDone: { target: 'loading', actions: assign({ notice: 'Recurring rule deleted.' }) },
        onError: { target: 'ready', actions: 'recordMutationError' },
      },
    },
    exceptionSaving: {
      invoke: {
        src: 'saveException',
        input: ({ event }) => ({
          exception: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_SAVE_EXCEPTION' }>)
            .exception,
          ruleId: (event as Extract<AvailabilityCalendarEvent, { type: 'ON_SAVE_EXCEPTION' }>)
            .ruleId,
        }),
        onDone: { target: 'loading', actions: assign({ notice: 'Exception saved.' }) },
        onError: { target: 'ready', actions: 'recordMutationError' },
      },
    },
  },
});
